import { computed, effect, signal, Signal, untracked, WritableSignal } from '@angular/core';
import { ObjectTools } from '../tools/object-tools';

/**
 * Reactive state container shared by `SignalHolder` (components) and `SubjectHolder` (services).
 *
 * The canonical state lives in one signal, set synchronously by `update`, so a caller reading `state` straight after an
 * update sees the new value. On top of that the holder derives one signal per state field ("slice"), created the first
 * time a `compute` mapper reads that field through the tracking proxy. `update` diffs the next state field by field: a
 * field that is deep-equal to the previous one keeps its previous reference, so its slice reports no change, only the
 * computeds that read a genuinely changed field re-run, and a child component bound to an unchanged field never sees a
 * new input.
 */
export abstract class StateHolder<S> {
  private readonly _state$: WritableSignal<S>;
  private readonly _slices = new Map<PropertyKey, Signal<unknown>>();
  private readonly _tracked: S;

  protected constructor(initialState: S) {
    this._state$ = signal<S>(initialState);
    this._tracked = this.createTracked();
  }

  /**
   * Return the store state as a (read-only) signal.
   */
  protected get stateSignal(): Signal<S> {
    return this._state$;
  }

  /**
   * Return the current store state.
   */
  public get state(): S {
    return this._state$();
  }

  /**
   * Return the current store state without registering a reactive dependency on it.
   */
  protected get currentState(): S {
    return untracked(this._state$);
  }

  /**
   * Updates the current state by applying the provided reducer function.
   * The reducer function takes the current state as input and returns a partial state object to be merged.
   * If the resulting state is equal to the current state, no update is performed.
   */
  protected update(reducer: (state: S) => Partial<S>): void {
    const current = this.currentState;
    const partial = reducer(current);
    if ((partial as unknown) === (current as unknown)) {
      return;
    }
    this.commitState(current, StateHolder.merge(current, partial));
  }

  /**
   * Patches a partial state into the current state, updating only if the result differs.
   */
  protected patch(partialState: Partial<S>): void {
    return this.update(() => partialState);
  }

  /**
   * Diffs the next state against the current one field by field and emits only when at least one field changed.
   * A field indistinguishable from its previous value keeps that previous reference.
   */
  protected commitState(current: S, next: S): void {
    if (!StateHolder.isObject(current) || !StateHolder.isObject(next)) {
      if (ObjectTools.differs(current, next)) {
        this._state$.set(next);
      }
      return;
    }

    const currentFields = current as Record<PropertyKey, unknown>;
    const nextFields = next as Record<PropertyKey, unknown>;
    let changed = false;
    for (const key of StateHolder.fieldKeys(currentFields, nextFields)) {
      const previousValue = currentFields[key];
      const nextValue = nextFields[key];
      if (previousValue === nextValue) {
        continue;
      }
      if (ObjectTools.interchangeable(previousValue, nextValue)) {
        nextFields[key] = previousValue;
        continue;
      }
      changed = true;
    }

    if (changed) {
      this._state$.set(next);
    }
  }

  /**
   * Create a computed signal, derived from the state and a given mapper function. The mapper reads the state through
   * the tracking proxy, so it only re-runs when one of the fields it read changed.
   */
  protected computeSignal<R>(mapper: (state: S) => R): Signal<R> {
    return computed(() => this.mapTracked(mapper), {
      equal: (a, b) => a === b || ObjectTools.equals(a, b),
    });
  }

  /**
   * Create a computed signal using reference equality (Object.is) instead of deep equality.
   * Use for signals extracting primitive or enum values where deep comparison is unnecessary.
   */
  protected computeSignalByRef<R>(mapper: (state: S) => R): Signal<R> {
    return computed(() => this.mapTracked(mapper));
  }

  /**
   * Converts the current state into an effect that reacts to state changes.
   */
  public watch(consumer: (state: S) => void): void {
    this.watchSignal(this.stateSignal, consumer);
  }

  /**
   * Converts the mapped state into an effect that reacts to changes of the mapped value only.
   */
  public watchComputed<R>(mapper: (state: S) => R, consumer: (state: R) => void): void {
    this.watchSignal(this.computeSignal(mapper), consumer);
  }

  /**
   * Runs the consumer for every value of the given signal.
   */
  protected watchSignal<R>(source: Signal<R>, consumer: (value: R) => void): void {
    effect(() => {
      const value = source();
      untracked(() => consumer(value));
    });
  }

  /**
   * Runs a mapper against the tracking proxy. A mapper that hands the state straight back read no field through the
   * proxy and so would depend on nothing; its value is the whole state, so it reads the canonical signal instead and
   * depends on every change — and returns the real instance, never the proxy.
   */
  private mapTracked<R>(mapper: (state: S) => R): R {
    if (!StateHolder.isObject(this.currentState)) {
      return mapper(this._state$());
    }
    const result = mapper(this._tracked);
    return (result as unknown) === (this._tracked as unknown) ? (this._state$() as unknown as R) : result;
  }

  /**
   * The proxy a `compute` mapper receives. Reading a data field of the state goes through that field's slice, so the
   * mapper depends on that field and not on the whole state — including a field the state does not carry yet, which a
   * later `patch` may add. Prototype members — methods and getters — are read from the current instance with the proxy
   * as receiver, so the fields they read in turn are tracked as well.
   */
  private createTracked(): S {
    return new Proxy({} as Record<PropertyKey, unknown>, {
      get: (_target, key, receiver) => this.trackedGet(key, receiver),
      has: (_target, key) => key in (this._state$() as object),
      ownKeys: () => Reflect.ownKeys(this._state$() as object),
      getOwnPropertyDescriptor: (_target, key) => {
        const descriptor = Reflect.getOwnPropertyDescriptor(this._state$() as object, key);
        return descriptor === undefined ? undefined : { ...descriptor, configurable: true };
      },
      getPrototypeOf: () => Reflect.getPrototypeOf(this._state$() as object),
    }) as S;
  }

  private trackedGet(key: PropertyKey, receiver: unknown): unknown {
    const current = this.currentState as object;
    if (!Object.prototype.hasOwnProperty.call(current, key) && key in current) {
      return Reflect.get(current, key, receiver);
    }
    return this.slice(key)();
  }

  private slice(key: PropertyKey): Signal<unknown> {
    const existing = this._slices.get(key);
    if (existing !== undefined) {
      return existing;
    }
    const created = computed(() => {
      const current = this._state$();
      return StateHolder.isObject(current) ? (current as Record<PropertyKey, unknown>)[key] : undefined;
    });
    this._slices.set(key, created);
    return created;
  }

  /**
   * Merges the reducer result into a prototype-preserving clone of the current state. A state that is not an object
   * cannot be cloned into, so there the reducer result is the next state as it stands.
   */
  protected static merge<S>(current: S, partial: Partial<S>): S {
    return StateHolder.isObject(current) ? ObjectTools.copy(current, partial) : (partial as S);
  }

  private static isObject(value: unknown): boolean {
    return value !== null && typeof value === 'object';
  }

  private static fieldKeys(current: Record<PropertyKey, unknown>, next: Record<PropertyKey, unknown>): string[] {
    const keys = Object.keys(current);
    for (const key of Object.keys(next)) {
      if (!Object.prototype.hasOwnProperty.call(current, key)) {
        keys.push(key);
      }
    }
    return keys;
  }
}
